export * from './types';
export * from './ids';
export * from './format';
export * from './i18n';
export * from './config';
export * from './supabase';
export * from './net';
export * from './storage';
export * from './data/categories';
export * from './data/items';
export * from './data/tables';
export * from './data/settings';
export * from './data/expenseCategories';
export * from './logic/pricing';
export * from './logic/analytics';
export * from './logic/expenses';
export * from './sync/types';
export * from './sync/outbox';
export * from './sync/engine';
export * from './staffAdmin';
export * from './store';

export const NAV = [
  { id: 'dashboard', labelKey: 'nav_dashboard', icon: 'dashboard' },
  { id: 'menu', labelKey: 'nav_menu', icon: 'menu' },
  { id: 'order', labelKey: 'nav_order', icon: 'order' },
  { id: 'orders', labelKey: 'nav_orders', icon: 'clock' },
  { id: 'customers', labelKey: 'nav_customers', icon: 'user' },
  { id: 'offers', labelKey: 'nav_offers', icon: 'offers' },
  { id: 'analytics', labelKey: 'nav_analytics', icon: 'analytics' },
] as const;

/**
 * Tab order for the bottom bar, most-used first. As many as fit are shown and
 * the remainder move behind "More".
 *
 * Eight tabs did not fit across a 390px phone, so the bar scrolled sideways —
 * which put half of them off-screen with nothing to say they were there. The
 * first three are what a counter touches through a shift: take the order,
 * check the recent ones, glance at the day. The rest are jobs you go looking
 * for, in the order you are likely to want them.
 *
 * This is deliberately NOT the same order as NAV, which is the desktop
 * sidebar's order. A sidebar is a map of the app and reads best grouped by
 * subject; a phone bar is a set of thumb targets and reads best by frequency.
 */
export const PHONE_TAB_ORDER: readonly string[] = [
  'dashboard', 'order', 'orders', 'menu', 'customers', 'offers', 'analytics',
  'expenses', 'staff',
];

/**
 * Roughly what one tab needs, in px, for an icon and its label.
 *
 * 90 puts four tabs across a 375px phone, which is what the layout was checked
 * at and reads comfortably. It is a budget, not a guarantee: NavItem's label
 * is numberOfLines={1}, so a language or text size that overruns it ellipsises
 * rather than pushing the bar out of shape — the failure mode is a shortened
 * word, never a tab you cannot reach.
 */
export const PHONE_TAB_WIDTH = 90;

/** The smallest bar we will draw, however narrow the device. */
const MIN_TABS = 3;

/**
 * Splits the tabs into the bar and the More list for a given width.
 *
 * A 390px phone fits three plus More; a 768px tablet in portrait fits most of
 * them, and showing only three there would waste half the bar and bury tabs
 * behind a tap for no reason. When everything fits, More is not drawn at all.
 */
export function splitPhoneTabs<T extends { id: string }>(
  navItems: readonly T[],
  width: number,
): { bar: T[]; more: T[] } {
  const order = (id: string) => {
    const i = PHONE_TAB_ORDER.indexOf(id);
    return i === -1 ? PHONE_TAB_ORDER.length : i;
  };
  const ranked = [...navItems].sort((a, b) => order(a.id) - order(b.id));
  const fits = Math.max(MIN_TABS, Math.floor(width / PHONE_TAB_WIDTH));

  if (fits >= ranked.length) return { bar: ranked, more: [] };
  // One slot goes to More itself, so the bar shows one fewer real tab.
  return { bar: ranked.slice(0, fits - 1), more: ranked.slice(fits - 1) };
}

/**
 * The phone-only tab that opens the list of everything else.
 *
 * Deliberately last, and deliberately the same for every role: an owner and a
 * worker see an identical bar, so nothing about its shape hints at what the
 * owner can reach.
 */
export const MORE_NAV = {
  id: 'more',
  labelKey: 'nav_more',
  icon: 'more',
} as const;

/**
 * Neither of these is in NAV: both are web-only and role-gated, so the shell
 * adds whichever one applies at runtime rather than showing a tab that then
 * refuses to work.
 *
 * They are deliberately different screens, not one screen with two labels. An
 * admin manages restaurants and appoints owners; an owner manages the workers
 * of the one shop they run and never sees the restaurant list.
 */
export const ADMIN_NAV = {
  id: 'admin',
  labelKey: 'nav_admin',
  icon: 'lock',
} as const;

export const STAFF_NAV = {
  id: 'staff',
  labelKey: 'nav_staff',
  icon: 'user',
} as const;

/**
 * What the shop spends. Owner-only, and added at runtime for the same reason
 * STAFF_NAV is: a tab a worker can see but not use is worse than no tab.
 *
 * Unlike the two above it is NOT web-only. An owner pays the vegetable seller
 * standing in the market, not at the counter, so it has to be on their phone —
 * and the role gate is what keeps it off the worker's phone.
 */
export const EXPENSES_NAV = {
  id: 'expenses',
  labelKey: 'nav_expenses',
  icon: 'wallet',
} as const;

/** view id -> [title key, subtitle key] */
export const TITLE_KEYS: Record<string, [string, string]> = {
  dashboard: ['title_dashboard', 'tag_dashboard'],
  menu: ['title_menu', 'tag_menu'],
  order: ['title_order', 'tag_order'],
  orders: ['title_orders', 'tag_orders'],
  offers: ['title_offers', 'tag_offers'],
  analytics: ['title_analytics', 'tag_analytics'],
  expenses: ['title_expenses', 'tag_expenses'],
  more: ['title_more', 'tag_more'],
  customers: ['title_customers', 'tag_customers'],
  admin: ['title_admin', 'tag_admin'],
  staff: ['title_staff', 'tag_staff'],
};
export * from './invoice';
